const toNodeArray = (value) => (
    Array.isArray(value)
        ? value
        : (value && typeof value === "object" ? Object.values(value) : [])
);

export const COLOR_NAME_TRAILING_CODE_REGEX = /\s+\d+(?:\s*[A-Za-z])?\s*$/u;

const hasRealPrintContent = (selection = null) => {
    if (!selection?.print) return false;
    const attrs = selection?.attrs || {};
    const print = selection?.print || {};
    const mode = String(selection?.mode || "").toLowerCase();
    const printKind = String(attrs?.print_kind || "").toLowerCase();
    const isPersonalText = String(attrs?.personal || "0") === "1";

    const textValue = String(attrs?.text || "").trim();
    if (mode === "text" || printKind === "text") {
        return isPersonalText || textValue.length > 0;
    }

    const hasImageUrl = !!String(attrs?.image_url || "").trim();
    const hasPredesignAsset = !!String(
        print?.img_full
        || print?.img
        || print?.icon_url
        || print?.img_url
        || print?.image_url
        || ""
    ).trim();

    if (printKind === "image_upload") return hasImageUrl;
    if (printKind === "predesign" || mode === "predesign") return hasPredesignAsset;
    if (mode === "image") return hasImageUrl || hasPredesignAsset;

    return hasImageUrl || hasPredesignAsset || textValue.length > 0;
};

export function sanitizeColorName(value, fallback = "Color") {
    const raw = String(value || "").trim();
    if (!raw) return fallback;
    const withoutTrailingCode = raw.replace(COLOR_NAME_TRAILING_CODE_REGEX, "").trim();
    return withoutTrailingCode || fallback;
}

const normalizeTreeNode = (node) => {
    if (!node || typeof node !== "object") return null;

    const item = node.Item || {};
    const placementValue = item.placement ?? item.posicion ?? node.placement ?? node.posicion ?? null;
    const normalizedItem = {
        ...item,
        name: item.name ?? node.name ?? item.nname ?? `Parte ${node.key}`,
        nname: item.nname ?? item.name ?? node.nice_name ?? node.name ?? `Parte ${node.key}`,
        z: item.z ?? node.z_index ?? node.z ?? 0,
        top: item.top ?? node.top ?? null,
        left: item.left ?? node.left ?? null,
        placement: placementValue,
        posicion: placementValue,
        display: item.display ?? node.display ?? null,
        img_icon: item.img_icon ?? node.img_icon ?? node.icon_url ?? null,
        img_front_t: item.img_front_t ?? node.img_front ?? null,
        img_front: item.img_front ?? node.img_front_shadow ?? null,
        img_back_t: item.img_back_t ?? node.img_back ?? null,
        img_back: item.img_back ?? node.img_back_shadow ?? null,
    };

    return {
        ...node,
        key: String(node.key),
        type: node.type ?? node.master_part_type ?? null,
        required: !!node.required,
        selected: !!node.selected,
        Item: normalizedItem,
        materials: toNodeArray(node.materials),
        prints: toNodeArray(node.prints),
        children: toNodeArray(node.children).map(normalizeTreeNode).filter(Boolean),
    };
};

export function normalizeRoot(masterTree) {
    if (!masterTree) return null;

    const directTree = masterTree?.tree
        ? (Array.isArray(masterTree.tree) ? masterTree.tree[0] : masterTree.tree)
        : null;

    if (directTree?.key) {
        return normalizeTreeNode(directTree);
    }

    if (masterTree.key) {
        return normalizeTreeNode(masterTree);
    }

    const parts = Array.isArray(masterTree)
        ? masterTree
        : (Array.isArray(masterTree.parts)
            ? masterTree.parts
            : (masterTree.parts ? Object.values(masterTree.parts) : []));
    if (!parts.length) return null;

    const byId = {};
    parts.forEach(p => {
        const isPrintLocation = Number(p.master_part_type) === MASTER_PART_TYPE.PRINTS;
        const placementValue = p.placement ?? p.posicion ?? null;
        const normalizedNiceName = isPrintLocation
            ? (p.master_parts?.nice_name ?? p.master_part?.nice_name ?? p.nname ?? p.nice_name ?? p.name)
            : (p.nname ?? p.nice_name ?? p.name);
        const printsArr = Array.isArray(p.prints)
            ? p.prints
            : (p.prints ? Object.values(p.prints) : []);
        const materialsArr = Array.isArray(p.materials)
            ? p.materials
            : (p.materials ? Object.values(p.materials) : []);
        const hasImgs = !!(p.img_front || p.img_back || p.img_front_shadow || p.img_back_shadow);
        const item = {
            name: p.name,
            nname: normalizedNiceName,
            z: p.z_index ?? p.z ?? 0,
            top: p.top ?? null,
            left: p.left ?? null,
            placement: placementValue,
            posicion: placementValue,
            b: p.b ?? p.side ?? (
                placementValue === 2 || placementValue === "2" || placementValue === 0 || placementValue === "0"
                    ? 1
                    : (placementValue === 1 || placementValue === "1" ? 0 : null)
            ),
            display: p.display ?? (hasImgs ? "block" : null),
            img_icon: p.img_icon ?? p.icon_url ?? null,
            img_front_t: p.img_front ?? null,
            img_front: p.img_front_shadow ?? null,
            img_back_t: p.img_back ?? null,
            img_back: p.img_back_shadow ?? null,
        };
        byId[p.id] = {
            key: p.id,
            type: p.master_part_type,
            required: !!p.required,
            name: p.name,
            Item: item,
            materials: materialsArr,
            prints: printsArr,
            children: [],
        };
    });

    const root = { key: 'master', type: 'root', Item: { name: 'Master' }, children: [] };
    parts.forEach(p => {
        const node = byId[p.id];
        const parent = p.parent_master_part_id && byId[p.parent_master_part_id] ? byId[p.parent_master_part_id] : root;
        parent.children.push(node);
    });

    return normalizeTreeNode(root);
}

export const MASTER_PART_TYPE = Object.freeze({
    PART: 1,
    EXTRA: 5,
    PRINTS: 6,
    PART_TYPE: 7,
    PART_PART: 8,
    PART_LEFT: 9,
    PART_RIGHT: 10,
});

export const MASTER_PART_TYPE_LABELS = Object.freeze({
    [MASTER_PART_TYPE.PART]: "Parte",
    [MASTER_PART_TYPE.PART_PART]: "Parte de parte",
    [MASTER_PART_TYPE.PART_TYPE]: "Tipo de parte",
    [MASTER_PART_TYPE.PART_LEFT]: "Parte izquierda",
    [MASTER_PART_TYPE.PART_RIGHT]: "Parte derecha",
    [MASTER_PART_TYPE.PRINTS]: "Ubicación de Diseño",
    [MASTER_PART_TYPE.EXTRA]: "Extra",
});

export const MASTER_PART_TYPE_ORDER = [
    MASTER_PART_TYPE.PART,
    MASTER_PART_TYPE.PART_TYPE,
    MASTER_PART_TYPE.PART_PART,
    MASTER_PART_TYPE.PART_LEFT,
    MASTER_PART_TYPE.PART_RIGHT,
    MASTER_PART_TYPE.PRINTS,
    MASTER_PART_TYPE.EXTRA,
];

export const MASTER_PRINT_TYPE = Object.freeze({
    EMBROIDERY: 1,
    SERIGRAPHY: 2,
    PATCH: 3,
});

export const MASTER_PRINT_TYPE_LABELS = Object.freeze({
    [MASTER_PRINT_TYPE.EMBROIDERY]: "Bordado",
    [MASTER_PRINT_TYPE.SERIGRAPHY]: "Serigrafía",
    [MASTER_PRINT_TYPE.PATCH]: "Aplique",
});

export const MASTER_PRINT_FONTS = Object.freeze({
    1: { id: 1, name: "All Star", family: "Allstar", file: "Allstar.ttf" },
    2: { id: 2, name: "Almelo", family: "FV_Almelo", file: "FV_Almelo.ttf" },
    3: { id: 3, name: "Ballpark Weiner", family: "BALLW___", file: "BALLW___.TTF" },
    4: { id: 4, name: "Cairo", family: "Cairo", file: "Cairo.ttf" },
    5: { id: 5, name: "College", family: "college", file: "college.ttf" },
    6: { id: 6, name: "Complete in him", family: "Complete", file: null },
    7: { id: 7, name: "Cowboys", family: "Cowboys", file: "Cowboys.ttf" },
    8: { id: 8, name: "DEPALMA", family: "DEPALMA", file: "DEPALMA.ttf" },
    9: { id: 9, name: "Freshman", family: "Freshman", file: "Freshman.ttf" },
    10:{ id:10, name: "Gladifil", family: "Gladifil", file: "Gladifil.ttf" },
    11:{ id:11, name: "Good Girl", family: "GOODGIRL", file: "GOODGIRL.TTF" },
    12:{ id:12, name: "Gunplay", family: "Gunplay", file: null },
    13:{ id:13, name: "Hipsteria", family: "Hipsteria", file: "Hipsteria.ttf" },
    14:{ id:14, name: "Holtwood", family: "Holtwood", file: "Holtwood.ttf" },
    15:{ id:15, name: "Home Run", family: "Home Run", file: null },
    16:{ id:16, name: "Jersey", family: "Jersey", file: null },
    17:{ id:17, name: "John Hancock", family: "JohnHancockCP", file: null },
    18:{ id:18, name: "Kitchen Police", family: "KitchenPolice", file: null },
    19:{ id:19, name: "Krinkes Decor", family: "KrinkesDecor", file: null },
    20:{ id:20, name: "Lauren", family: "LaurenScript", file: null },
    21:{ id:21, name: "Lawson", family: "LAWSON", file: "LAWSON.ttf" },
    22:{ id:22, name: "Mari & David", family: "MARIDAVID", file: "MARIDAVID.ttf" },
    23:{ id:23, name: "Moon Flower Bold", family: "MoonFlowerBold", file: "MoonFlowerBold.ttf" },
    24:{ id:24, name: "Moyko", family: "Moyko", file: null },
    25:{ id:25, name: "Oce slab", family: "Oce", file: "oce.ttf" },
    26:{ id:26, name: "Om telolet Om", family: "OmTelolet", file: null },
    27:{ id:27, name: "Playfair", family: "PlayfairDisplay-Regular", file: null },
    28:{ id:28, name: "Pricedown", family: "PRICEDOW", file: "PRICEDOW.ttf" },
    29:{ id:29, name: "Roboto Black", family: "Roboto-Black", file: "Roboto-Black.ttf" },
    30:{ id:30, name: "Roboto", family: "Roboto-Regular", file: "Roboto-Regular.ttf" },
    31:{ id:31, name: "Simple Life", family: "SimpleLife", file: "SimpleLife.ttf" },
    32:{ id:32, name: "Simpsonfont", family: "Simpsonfont", file: null },
    33:{ id:33, name: "Snickles", family: "Snickles", file: "Snickles.ttf" },
    34:{ id:34, name: "Stencil", family: "Stencil", file: "Stencil.ttf" },
    35:{ id:35, name: "Thug", family: "Thug", file: null },
    36:{ id:36, name: "TypeWrong", family: "typwrng", file: "typwrng.ttf" },
    37:{ id:37, name: "Varsity", family: "Varsity", file: null },
    38:{ id:38, name: "Vexler", family: "vexler-slip", file: "vexler-slip.ttf" },
    39:{ id:39, name: "Year2000Boogie", family: "Year2000Boogie", file: "Year2000Boogie.ttf" },
    40:{ id:40, name: "Times New Roman", family: "Times New Roman", file: null },
    41:{ id:41, name: "Arial", family: "Arial", file: null },
    42:{ id:42, name: "Molot", family: "Molot", file: "Molot.ttf" },
    43:{ id:43, name: "Hiruko", family: "HirukoBlackAlternate", file: "HirukoBlackAlternate.ttf" },
    44:{ id:44, name: "American Typewriter", family: "typewriter", file: "typewriter.ttf" },
});

export const MASTER_PRINT_FONTS_BY_TYPE = Object.freeze({
    [MASTER_PRINT_TYPE.EMBROIDERY]: [3,5,16,1,2,10,13,19,20,21,22,23,24,25,26,27,30,31,32,33,35,6,40,41],
    [MASTER_PRINT_TYPE.SERIGRAPHY]: [3,5,16,1,2,10,13,43,19,20,21,24,25,26,27,30,31,6,4,7,8,9,11,12,14,15,18,28,36,38,34,30,37,17,40,41,32,35,39],
    [MASTER_PRINT_TYPE.PATCH]: [3,4,5,7,8,9,10,11,12,13,14,15,16,18,28,29,36,38,34],
});

// Fuentes del diseñador servidas por V4 (las 53 copiadas de v3core a su public/fonts/master).
// El dueño aprobó el CORS en nginx el 2-10-2026 (map $http_origin acotado a los orígenes propios:
// alumnos/qa.micuenta.blue, littleblue.com.ar), así que el @font-face cross-origin ya resuelve.
// El bundle same-origin de este repo solo tenía 21 de 44; con V4 vuelven todas.
// TODO: el dueño quiere administrar las fuentes desde el ABM de V4 → migrar a GET /api/design/fonts
// y jubilar la tabla hardcodeada MASTER_PRINT_FONTS (ojo: ese endpoint sirve /uploads/typographies,
// NO /fonts/master, y family≠name / matchear por legacy_id). Ver memoria disenador-escala-prints.
export const MASTER_PRINT_FONT_BASE_URL =
    (process.env.REACT_APP_ASSET_URL || "https://v4.micuenta.blue") + "/fonts/master/";

// ───────────────────────────────────────────────────────────────────────────
// Tipografías DINÁMICAS (ABM de V4, GET /api/design/fonts). El hook
// useDesignerRemoteData llama a setDesignFonts() al abrir el diseñador; mientras
// no haya cargado (o si el fetch falla), TODO cae a MASTER_PRINT_FONTS (la tabla
// de arriba) → nunca queda peor que hoy.
//
// 🪤 El id que guardan las selecciones y los bocetos VIEJOS es el id de V3 =
// `legacy_id` del endpoint. Los ids nuevos del ABM son ≥45 y los legacy ≤44, así
// que no se pisan: resolvemos por legacy_id primero y por id nuevo después.
// ───────────────────────────────────────────────────────────────────────────
let DYNAMIC_FONTS_LIST = null;      // array normalizado, o null si no cargó
let DYNAMIC_FONTS_BY_KEY = null;    // Map(string -> fontDef): clave por legacy_id Y por id

const TYPE_TO_TECHNIQUES = Object.freeze({
    [MASTER_PRINT_TYPE.EMBROIDERY]: ["bordado"],
    [MASTER_PRINT_TYPE.SERIGRAPHY]: ["serigrafia", "dtf"],
    [MASTER_PRINT_TYPE.PATCH]: ["aplique"],
});

const normalizeApiFont = (f = {}) => {
    const dbId = Number(f.id);
    const legacyId = f.legacy_id != null && f.legacy_id !== "" ? Number(f.legacy_id) : null;
    return {
        // `id` = id ESTABLE para guardar/matchear en las selecciones: el legacy (lo que ya guardan
        // los bocetos viejos) si existe, si no el id nuevo del ABM. El archivo (file/file_url) usa
        // el id de DB real aparte, así que no se mezcla.
        id: legacyId != null ? legacyId : dbId,
        legacy_id: legacyId,
        db_id: dbId,
        name: String(f.name || f.family || ""),
        family: String(f.family || f.name || "Arial"),
        file: f.file || null,
        file_url: f.file_url || null,
        scale: Number(f.scale) > 0 ? Number(f.scale) : 100,
        for: Array.isArray(f.for) ? f.for.map((t) => String(t).toLowerCase()) : [],
    };
};

export const setDesignFonts = (fonts = []) => {
    const list = (fonts || [])
        .map(normalizeApiFont)
        .filter((f) => Number.isFinite(f.id) && (f.file_url || f.file));
    if (!list.length) return;
    const byKey = new Map();
    list.forEach((f) => { byKey.set(String(f.id), f); });
    DYNAMIC_FONTS_LIST = list;
    DYNAMIC_FONTS_BY_KEY = byKey;
};

// Lista completa de fuentes para el selector del wizard (todas, sin filtrar por técnica),
// con fallback a la tabla fija cuando el ABM no cargó.
export const getAllDesignFonts = () => {
    if (hasDesignFonts()) return DYNAMIC_FONTS_LIST;
    return Object.values(MASTER_PRINT_FONTS).filter((f) => f?.file);
};

export const hasDesignFonts = () => Array.isArray(DYNAMIC_FONTS_LIST) && DYNAMIC_FONTS_LIST.length > 0;

// Resuelve el font guardado (id de V3/legacy o id nuevo) a su definición. Cae a la tabla fija.
export const getFontById = (id) => {
    const key = String(id ?? "").trim();
    if (!key) return null;
    if (DYNAMIC_FONTS_BY_KEY && DYNAMIC_FONTS_BY_KEY.has(key)) return DYNAMIC_FONTS_BY_KEY.get(key);
    return MASTER_PRINT_FONTS[key] || null;
};

// URL del archivo .ttf/.otf para el @font-face. Las del ABM traen file_url absoluto
// (/uploads/typographies); la tabla fija usa file relativo + base (/fonts/master).
export const getFontFaceUrl = (font) => {
    if (!font) return null;
    if (font.file_url) return font.file_url;
    if (font.file) return `${MASTER_PRINT_FONT_BASE_URL}${font.file}`;
    return null;
};

// Escala por fuente definida en el ABM (%, default 100). La tabla fija no tiene → 100.
export const getFontScale = (font) => {
    const s = Number(font?.scale);
    return Number.isFinite(s) && s > 0 ? s : 100;
};

export function getFontsForPrintType(type) {
    const techniques = TYPE_TO_TECHNIQUES[type] || [];
    if (hasDesignFonts() && techniques.length) {
        const matched = DYNAMIC_FONTS_LIST.filter((f) => f.for.some((t) => techniques.includes(t)));
        if (matched.length) return matched;
    }
    const ids = MASTER_PRINT_FONTS_BY_TYPE[type] || [];
    return ids.map(id => MASTER_PRINT_FONTS[id]).filter(Boolean);
}

export const MASTER_PRINT_COLOR_SLOTS = Object.freeze({
    [MASTER_PRINT_TYPE.SERIGRAPHY]: 3,
    [MASTER_PRINT_TYPE.EMBROIDERY]: 2,
    [MASTER_PRINT_TYPE.PATCH]: 2,
});

export const getPrintColorSlots = (printType) =>
    MASTER_PRINT_COLOR_SLOTS[printType] ?? 1;

export function buildParentByKey(normalizedRoot) {
    const map = {};
    const walk = (node, parent = null) => {
        if (!node) return;
        const k = String(node.key);
        map[k] = parent;
        (node.children || []).forEach(child => walk(child, k));
    };
    if (normalizedRoot) walk(normalizedRoot, null);
    return map;
}

export function buildFullSelectionTree(node, selected) {
    const isSelected = selected.has(String(node.key));
    const children = (node.children || []).map(child => buildFullSelectionTree(child, selected));
    const payload = { key: String(node.key) };
    if (isSelected) payload.selected = true;
    if (children.length) payload.children = children;
    return payload;
}

export function buildPartsList(normalizedRoot) {
    if (!normalizedRoot) return [];
    const items = [];
    const walk = (node, parentLabel = null) => {
        const label = node?.Item?.nname || node?.Item?.name || `Parte ${node.key}`;
        if (node.key !== 'master') items.push({ key: String(node.key), label, type: node.type, parent: parentLabel });
        (node.children || []).forEach(child => walk(child, label));
    };
    walk(normalizedRoot);
    return items;
}

export function collectMaterials(normalizedRoot) {
    const map = new Map();
    const walk = (node) => {
        if (!node) return;
        (node.materials || []).forEach((m) => {
            if (!map.has(m.id)) {
                map.set(m.id, { id: m.id, name: m.name, material_type: m.material_type, type: m.type });
            }
        });
        (node.children || []).forEach(walk);
    };
    walk(normalizedRoot);
    return Array.from(map.values());
}

export const WEB_MATERIAL_TYPE = Object.freeze({
    FABRIC_SMOOTH: 13,
    FABRIC_PRINTED: 14,
    FABRIC_OTHER: 15,
    MATERIAL_SMOOTH: 4,
    MATERIAL_OTHER: 11,
});

export function isNodeSublimable(node) {
    if (!node) return false;
    if (node?.Item?.sublimable) return true;
    const materials = Array.isArray(node.materials) ? node.materials : Object.values(node.materials || {});
    return materials.some((m) => Number(m.material_type) === WEB_MATERIAL_TYPE.FABRIC_PRINTED || Number(m.type) === WEB_MATERIAL_TYPE.FABRIC_PRINTED);
}

export function buildAppliedPartsPreview({
    root,
    selectedKeys,
    selectedPrints,
    selectedColors,
    selectedMaterials = {},
    partAttrsByKey = {},
    generalMaterialId = null,
    selectedFullprints = {},
    partSublimation = {},
}) {
    if (!root) return [];
    const partMap = {};

    (selectedKeys ? Array.from(selectedKeys) : []).forEach((k) => {
        const key = String(k);
        if (key === "master") return;
        partMap[key] = partMap[key] || {
            master_part_id: key,
            master_print_id: null,
            material_id: null,
            color_chart_id: null,
            color_id: null,
            attrs: {},
        };
    });

    Object.entries(selectedColors || {}).forEach(([selKey, colorObj]) => {
        if (selKey.includes("print-color")) return;
        const [partKey, materialId] = selKey.split("-");
        if (!partKey || typeof materialId === "undefined") return;
        if (!partMap[partKey]) return;
        const matId = materialId === "general" ? null : (isNaN(Number(materialId)) ? materialId : Number(materialId));
        partMap[partKey].material_id = matId;
        const ccId = colorObj?.color_chart_id ?? colorObj?.id ?? null;
        partMap[partKey].color_chart_id = ccId === null ? null : (isNaN(Number(ccId)) ? ccId : Number(ccId));
        const cid = colorObj?.color_id ?? colorObj?.id ?? null;
        partMap[partKey].color_id = cid === null ? null : (isNaN(Number(cid)) ? cid : Number(cid));
        partMap[partKey].attrs = {
            ...partMap[partKey].attrs,
            color: colorObj?.id != null ? String(colorObj.id) : null,
        };
    });

    const extraPrintRows = [];
    const printRowsCountByLocation = {};
    Object.entries(selectedPrints || {}).forEach(([rawPrintKey, data]) => {
        if (!hasRealPrintContent(data)) return;
        const locationKey = String(data?.attrs?.location_key || rawPrintKey).split("::")[0];
        let print = data.print;
        if (!print) {
            const node = findNode(root, locationKey);
            if (node && Array.isArray(node.prints) && node.prints.length) {
                print = node.prints[0];
            }
        }
        if (!print) return;
        const seenCount = Number(printRowsCountByLocation[locationKey] || 0);
        printRowsCountByLocation[locationKey] = seenCount + 1;
        const target =
            seenCount === 0
                ? (partMap[locationKey] = partMap[locationKey] || {
                    master_part_id: String(locationKey),
                    material_id: null,
                    color_chart_id: null,
                    color_id: null,
                    attrs: {},
                })
                : {
                    master_part_id: String(locationKey),
                    material_id: null,
                    color_chart_id: null,
                    color_id: null,
                    attrs: {},
                };

        const resolvedPrintType = Number(data?.attrs?.resolved_master_print_type || print?.master_print_type || 0) || null;
        let resolvedPrintId = Number(print?.id || 0);
        const currentPrintType = Number(print?.master_print_type || 0) || null;
        // Escudo/prediseño ELEGIDO de la galería: se manda SIEMPRE su master_print_id; V4 resuelve
        // técnica/subtipo/nombre/tela desde ahí. La técnica derivada (ej. remera→DTF) puede diferir
        // del tipo base del escudo → NO descartar su id por eso (antes caía a un print default).
        const isChosenPredesign = String(data?.attrs?.print_kind || "").toLowerCase() === "predesign"
            && Number(print?.id || 0) > 0;
        if (!isChosenPredesign && resolvedPrintType && currentPrintType && resolvedPrintType !== currentPrintType) {
            resolvedPrintId = 0;
        }
        if (!Number.isFinite(resolvedPrintId) || resolvedPrintId <= 0) {
            const node = findNode(root, locationKey);
            const fallbackPrint =
                (node?.prints || []).find(
                    (candidate) =>
                        Number(candidate?.id) > 0
                        && (
                            !resolvedPrintType
                            || Number(candidate?.master_print_type || 0) === resolvedPrintType
                        )
                )
                || (node?.prints || []).find((candidate) => Number(candidate?.id) > 0)
                || null;
            resolvedPrintId = Number(fallbackPrint?.id || 0);
        }
        target.master_print_id = Number.isFinite(resolvedPrintId) && resolvedPrintId > 0 ? resolvedPrintId : null;
        target.master_print_type = resolvedPrintType;
        target.attrs = {
            ...(target.attrs || {}),
            ...(data.attrs || {}),
            ...(resolvedPrintType ? { master_print_type: String(resolvedPrintType) } : {}),
        };

        if (seenCount > 0) {
            extraPrintRows.push(target);
        }
    });

    // apply explicit material selections
    Object.entries(selectedMaterials || {}).forEach(([partKey, matId]) => {
        if (partMap[partKey]) {
            partMap[partKey].material_id = isNaN(Number(matId)) ? matId : Number(matId);
        }
    });

    if (generalMaterialId) {
        Object.values(partMap).forEach((row) => {
            if (!row.material_id) {
                const node = findNode(root, row.master_part_id);
                const compatible = (node?.materials || []).some(m => String(m.id) === String(generalMaterialId));
                if (compatible) {
                    row.material_id = generalMaterialId;
                }
            }
        });
    }

    // apply fullprints for sublimated parts
    Object.entries(selectedFullprints || {}).forEach(([partKey, fp]) => {
        if (!partMap[partKey]) return;
        const fpId = fp?.id ?? fp?.value ?? fp?.code ?? null;
        if (!fpId && fpId !== 0) return;
        const fullprintCode = `f${String(fpId).replace(/^f/i, "")}`;
        partMap[partKey].color_chart_id = null;
        partMap[partKey].color_id = fullprintCode;
        partMap[partKey].attrs = {
            ...(partMap[partKey].attrs || {}),
            color: fullprintCode,
            fullprint: "1",
            fullprint_icon: fp?.icon || fp?.icon_url || fp?.iconUrl || null,
            sublimable: true,
        };
    });

    // flag sublimated parts even without selected fullprint
    Object.entries(partSublimation || {}).forEach(([partKey, enabled]) => {
        if (!enabled || !partMap[partKey]) return;
        partMap[partKey].attrs = {
            ...(partMap[partKey].attrs || {}),
            sublimable: true,
        };
    });

    Object.entries(partAttrsByKey || {}).forEach(([partKey, attrs]) => {
        if (!partMap[partKey] || !attrs || typeof attrs !== "object") return;
        const normalizedAttrs = {};
        Object.entries(attrs).forEach(([k, v]) => {
            if (v === undefined || v === null || v === "") return;
            normalizedAttrs[String(k)] = String(v);
        });
        if (!Object.keys(normalizedAttrs).length) return;
        partMap[partKey].attrs = {
            ...(partMap[partKey].attrs || {}),
            ...normalizedAttrs,
        };
    });

    return [...Object.values(partMap), ...extraPrintRows];
}

export function findNode(node, key) {
    if (!node) return null;
    if (String(node.key) === String(key)) return node;
    for (const child of node.children || []) {
        const found = findNode(child, key);
        if (found) return found;
    }
    return null;
}
